import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(v || 0);

// Storno oder Korrektur einer versendeten Honorarnote — Versand an die Kanzlei erfolgt sofort.
export const LexStornoDialog = ({ invoice, mode, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const isKorr = mode === "korrektur";

  const submit = async () => {
    setBusy(true);
    try {
      const url = `${API}/lex/honorar/${invoice.id}/${isKorr ? "korrektur" : "storno"}`;
      const body = isKorr ? { reason, amount: parseFloat(String(amount).replace(",", ".")), description: description || null } : { reason };
      const res = await axios.post(url, body);
      const d = res.data;
      const docs = isKorr ? `Storno ${d.storno_number} + neue Note ${d.new_number}` : `Storno-Note ${d.storno_number}`;
      if (d.email_sent) toast.success(`${docs} erstellt und an ${d.recipient} versendet.`);
      else toast.warning(`${docs} erstellt – der E-Mail-Versand an die Kanzlei ist fehlgeschlagen. Bitte PDF manuell senden.`);
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const valid = reason.trim() && (!isKorr || parseFloat(String(amount).replace(",", ".")) > 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="lex-theme max-w-md" data-testid="lex-storno-dialog">
        <DialogHeader>
          <DialogTitle className="lex-serif text-2xl" style={{ color: "#0A1128" }}>
            {isKorr ? "Honorarnote korrigieren" : "Honorarnote stornieren"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {isKorr
              ? <>Die Note <span className="lex-mono">{invoice.invoice_number}</span> ({fmtEur(invoice.total)}) wird storniert und durch eine neue Note ersetzt. Beide gehen sofort an Ihre Kanzlei.</>
              : <>Die Note <span className="lex-mono">{invoice.invoice_number}</span> ({fmtEur(invoice.total)}) wird vollständig aufgehoben. Die Storno-Note geht sofort an Ihre Kanzlei.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
              {isKorr ? "Korrekturgrund" : "Stornogrund"}
            </label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200}
              placeholder={isKorr ? "z. B. Betrag falsch abgerechnet" : "z. B. irrtümlich ausgestellt"}
              className="w-full border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]"
              data-testid="lex-storno-reason" />
          </div>
          {isKorr && (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Neuer Nettobetrag (€)</label>
                <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal"
                  className="w-full border border-slate-200 px-3 py-2 text-sm lex-mono focus:outline-none focus:border-[#C5A059]"
                  data-testid="lex-korrektur-amount" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Leistungsbeschreibung (optional)</label>
                <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200}
                  placeholder="Leer lassen = wie bisher"
                  className="w-full border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]"
                  data-testid="lex-korrektur-description" />
              </div>
            </>
          )}
          <p className="text-xs text-slate-400">
            Der Grund erscheint auf dem PDF. Steuerlich wirkt die Korrektur in der Periode, in der die Rückzahlung bzw. Zahlung erfolgt.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="text-xs uppercase tracking-widest text-slate-500 px-4 py-2"
              data-testid="lex-storno-cancel">Abbrechen</button>
            <button type="button" onClick={submit} disabled={!valid || busy} className="lex-btn !px-5 disabled:opacity-50"
              data-testid="lex-storno-submit">
              {busy ? "Wird erstellt …" : (isKorr ? "Korrigieren & senden" : "Stornieren & senden")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LexStornoDialog;
